import { HirerBudgetUpsell } from '@seek/hirer-budget-upsell';
import React, {
  type FunctionComponent,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { HIRER_PURCHASE_TYPE_CONTRACT } from 'src/app/constants/tealium';
import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import {
  actionResetAdBudget,
  actionUpdateAdBudget,
} from 'src/app/state/modules/session/adBudget';
import {
  actionRemoveCartLineItem,
  actionUpdateCartLineItem,
} from 'src/app/state/modules/session/cart';
import {
  actionResetAdBudgetValue,
  actionUpdateAdBudgetValue,
} from 'src/app/state/modules/tealium/adBudgetValue';
import {
  actionResetContractProductName,
  actionUpdateContractProductName,
} from 'src/app/state/modules/tealium/contractProductName';
import {
  actionResetContractProductSetProductId,
  actionUpdateContractProductSetProductId,
} from 'src/app/state/modules/tealium/contractProductSetProductId';
import {
  actionResetHirerPurchaseType,
  actionUpdateHirerPurchaseType,
} from 'src/app/state/modules/tealium/hirerPurchaseType';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { log } from 'src/app/utils/logging';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { useHubbleTrackEventHandler } from 'src/app/views/hooks/useHubbleTrackEventHandler';
import type { Tealium } from 'src/types/appState';
import type AppState from 'src/types/appState';
import type { TealiumEvent, TrackingEvent } from 'src/types/trackingData';

interface AdBudgetUpsellProps {
  onLoading?: (loading: boolean) => void;
}

export const AdBudgetUpsell: FunctionComponent<AdBudgetUpsellProps> = ({
  onLoading,
}) => {
  const {
    adBudget,
    country,
    draftId,
    cart,
    isPaymentInProgress,
    productSetId,
    selectedProductId,
    seekAdvertiserId,
    tealium,
  } = useSelector(
    (state: AppState) => ({
      adBudget: state.session.adBudget,
      country: state.session.hirerAccount.nation,
      draftId: state.session.draftId,
      isPaymentInProgress: state.session.isPaymentInProgress,
      productSetId: state.session.productSet?.id,
      selectedProductId: state.formData.selectedProductId,
      seekAdvertiserId: state.session.seekAdvertiserId,
      tealium: state.tealium,
      cart: state.session.cart,
    }),
    shallowEqual,
  );

  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const dispatch = useDispatch();

  const { getHubbleTrackEventHandler } = useHubbleTrackEventHandler();
  const { getLocale } = useConfig();

  const tealiumRef = useRef<Tealium>(tealium);

  tealiumRef.current = tealium;

  const onTrackingCallback = useCallback(
    (eventData: unknown) => {
      if (isPageAnalyticsEnabled) {
        getTrackingAdapter.sendLinkEvent(eventData as TrackingEvent);
      } else {
        sendLinkEvent(tealiumRef.current, eventData as TealiumEvent);
      }
    },
    [isPageAnalyticsEnabled],
  );

  // handles scenario when user goes back from PayAndPost to edit job details and creates new product set
  // or continue draft when budget was previously selected
  useEffect(() => {
    if (adBudget?.id) {
      const budgetLineItem = cart.lineItems.find(
        (lineItem) => lineItem.productId === adBudget.id,
      );

      if (
        (budgetLineItem && budgetLineItem.productSetId !== productSetId) ||
        (adBudget.id && !budgetLineItem) // continue draft scenario - selection is present but not in cart, add it
      ) {
        dispatch(
          actionUpdateCartLineItem({
            type: 'Budget',
            productSetId: productSetId!,
            productId: adBudget.id,
          }),
        );
      }
    }
  }, [productSetId, adBudget, cart.lineItems, dispatch]);

  const onBudgetOptionClicked = (
    budgetValue: number,
    interactionType: string,
  ) => {
    if (isPaymentInProgress) {
      return;
    }

    if (interactionType === 'select') {
      dispatch(actionUpdateAdBudgetValue((budgetValue / 100).toString()));
      dispatch(actionUpdateHirerPurchaseType(HIRER_PURCHASE_TYPE_CONTRACT));
    }
    if (interactionType === 'deselect') {
      dispatch(actionResetAdBudgetValue());
      dispatch(actionResetHirerPurchaseType());
    }
  };

  const onSelectedBudgetChange = (
    budgetProductId: string | null,
    budgetName: string | null,
  ) => {
    if (isPaymentInProgress) {
      return;
    }

    // scenario when user selects a budget on upsell widget or changes selection to a different budget
    if (budgetProductId && budgetProductId !== adBudget?.id) {
      dispatch(actionUpdateContractProductSetProductId(budgetProductId));
      dispatch(actionUpdateContractProductName(budgetName ?? ''));
      dispatch(
        actionUpdateAdBudget({
          id: budgetProductId,
        }),
      );

      if (adBudget?.id) {
        dispatch(actionRemoveCartLineItem(adBudget.id));
      }

      dispatch(
        actionUpdateCartLineItem({
          type: 'Budget',
          productSetId: productSetId!,
          productId: budgetProductId,
        }),
      );
    }

    // scenario when user deselects a budget on upsell widget
    if (!budgetProductId && adBudget?.id) {
      dispatch(actionResetAdBudget());
      dispatch(actionRemoveCartLineItem(adBudget.id));
      dispatch(actionResetContractProductSetProductId());
      dispatch(actionResetContractProductName());
    }
  };

  const onErrorHandler = (error: Error) => {
    log.error(
      'AdBudgetUpsell | Upsell | onError',
      {
        error,
        adBudget,
        draftId,
        selectedProductId,
        seekAdvertiserId,
      },
      true,
    );
  };

  return (
    <HirerBudgetUpsell
      advertiserId={seekAdvertiserId}
      selectedBudgetProductId={adBudget?.id ?? null}
      productSetId={productSetId!}
      productIds={[selectedProductId!]}
      locale={getLocale()}
      country={country}
      onLoading={onLoading}
      onSelectedBudgetChange={onSelectedBudgetChange}
      onTrackingCallback={onTrackingCallback}
      hubbleTrackEventHandler={getHubbleTrackEventHandler()}
      onBudgetOptionClicked={onBudgetOptionClicked}
      styles={{ paddingY: 'large', paddingX: 'gutter' }}
      logger={log}
      onError={onErrorHandler}
      useUnifiedTracking={isPageAnalyticsEnabled}
    />
  );
};
